import React, { useState, useEffect } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, useStripe, useElements, PaymentElement } from '@stripe/react-stripe-js';
import PropTypes from 'prop-types';
import { db } from '../firebaseConfig';
import { doc, getDoc } from 'firebase/firestore';

// Payment component that uses Elements
function PaymentForm({ 
  clientSecret, 
  uid, 
  selectedPlan, 
  subscriptionId,
  savedCardDetails,
  onUpgradeComplete, 
  onUpgradeError,
  onPaymentFormToggle 
}) {
  const stripe = useStripe();
  const elements = useElements();
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [paymentElementReady, setPaymentElementReady] = useState(false);

  // Set up PaymentElement event listeners
  useEffect(() => {
    if (!elements) return;

    const paymentElement = elements.getElement('payment');
    if (paymentElement) {
      paymentElement.on('ready', () => {
        console.log('✅ PaymentElement is ready!');
        setPaymentElementReady(true);
      });

      paymentElement.on('change', (event) => {
        if (event.error) {
          setError(event.error.message);
        } else {
          setError(null);
        }
      });
    }
  }, [elements]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!stripe || !elements || !clientSecret) {
      setError('Payment system not ready');
      return;
    }

    setIsProcessing(true);
    setError(null);

    try {
      const { error: confirmError } = await stripe.confirmPayment({
        elements,
        clientSecret,
        confirmParams: {
          return_url: window.location.origin
        },
        redirect: 'if_required'
      });

      if (confirmError) {
        throw new Error(confirmError.message);
      }

      console.log('✅ Payment confirmed successfully');
      
      // Complete the subscription and update Firebase
      const completeResponse = await fetch('/api/complete-subscription', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          uid,
          subscriptionId
        })
      });

      const completeResult = await completeResponse.json();
      if (!completeResponse.ok) {
        throw new Error(completeResult.error || 'Failed to complete subscription');
      }

      onUpgradeComplete?.(completeResult);
      
    } catch (err) {
      console.error('Payment error:', err);
      setError(err.message);
      onUpgradeError?.(err);
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="w-full max-w-3xl mx-auto space-y-3">
      <form onSubmit={handleSubmit} className="w-full space-y-3">
        <div className="p-6 rounded-xl bg-white/5 border border-white/10 w-full min-w-[400px]">
          <h3 className="text-white font-medium text-lg mb-4">Payment Information</h3>
          <div className="w-full min-w-[300px] max-w-[600px] mx-auto">
            <PaymentElement 
              options={{
                layout: "tabs",
                ...(savedCardDetails && {
                  defaultValues: {
                    paymentMethod: savedCardDetails.paymentMethodId
                  }
                }),
                appearance: {
                  theme: 'night',
                  variables: {
                    colorPrimary: '#6366f1',
                    colorBackground: 'rgba(255, 255, 255, 0.05)',
                    colorText: '#ffffff',
                    colorDanger: '#ef4444',
                    fontFamily: 'system-ui, sans-serif',
                    spacingUnit: '6px',
                    borderRadius: '12px',
                  },
                  rules: {
                    '.Input': {
                      backgroundColor: 'rgba(255, 255, 255, 0.1)',
                      border: '1px solid rgba(255, 255, 255, 0.2)',
                      color: '#ffffff',
                      padding: '14px 16px',
                      width: '100%',
                      minWidth: '300px',
                      maxWidth: '600px',
                    },
                    '.Input:focus': {
                      border: '1px solid #6366f1',
                      boxShadow: '0 0 0 2px rgba(99, 102, 241, 0.2)',
                    },
                    '.Tab': {
                      backgroundColor: 'rgba(255, 255, 255, 0.05)',
                      border: '1px solid rgba(255, 255, 255, 0.1)',
                      color: '#ffffff',
                      padding: '10px 16px',
                    },
                    '.Tab--selected': {
                      backgroundColor: 'rgba(99, 102, 241, 0.3)',
                      border: '1px solid rgba(99, 102, 241, 0.5)',
                    },
                    '.Block': {
                      width: '100%',
                      minWidth: '300px',
                    },
                    '.TabContainer': {
                      width: '100%',
                      minWidth: '300px',
                    },
                    '.Row': {
                      width: '100%',
                      minWidth: '300px',
                    }
                  }
                }
              }}
            />
          </div>
        </div>

        {error && (
          <div className="p-4 rounded-xl bg-red-500/20 border border-red-500/30">
            <p className="text-red-200 text-sm">{error}</p>
          </div>
        )}

        <div className="flex justify-center">
          <button
            type="submit"
            disabled={!paymentElementReady || isProcessing}
            className={`
              py-4 px-8 rounded-xl font-medium transition-all duration-200 flex items-center gap-2
              ${!isProcessing && paymentElementReady
                ? 'bg-green-500/40 border border-green-400/50 text-white hover:bg-green-500/50 shadow-lg shadow-green-500/20'
                : 'bg-gray-500/20 border border-gray-500/30 text-gray-400 cursor-not-allowed'
              }
            `}
          >
            {isProcessing ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                Processing...
              </>
            ) : (
              <>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                Pay {selectedPlan?.displayPrice || ''}
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
}

export default function StripeUpgradeButton({ 
  uid, 
  selectedPlan, 
  isUpgrading, 
  onUpgradeStart, 
  onUpgradeComplete, 
  onUpgradeError,
  onPaymentFormToggle,
  stripePromise
}) {
  
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [statusMessage, setStatusMessage] = useState('');
  const [customerId, setCustomerId] = useState(null);
  const [hasDefaultPaymentMethod, setHasDefaultPaymentMethod] = useState(false);
  const [savedCardDetails, setSavedCardDetails] = useState(null);
  const [clientSecret, setClientSecret] = useState(null);
  const [subscriptionId, setSubscriptionId] = useState(null);

  // Reset component state when needed (e.g., when modal reopens)
  useEffect(() => {
    setShowPaymentForm(false);
    setIsProcessing(false);
    setError(null);
    setStatusMessage('');
    setClientSecret(null);
    setSubscriptionId(null);
    // Don't reset customer/payment method data as that's still valid
  }, [uid, selectedPlan?.id]); // Reset when user or plan changes

  // Load user's Stripe customer info and check for existing payment methods
  useEffect(() => {
    const loadCustomerInfo = async () => {
      if (!uid) return;

      try {
        const userSubscriptionRef = doc(db, 'userSubscriptions', uid);
        const userSubscriptionDoc = await getDoc(userSubscriptionRef);
        
        if (userSubscriptionDoc.exists()) {
          const userData = userSubscriptionDoc.data();
          const stripeId = userData.stripeId;
          setCustomerId(stripeId);

          // Check if user has payment method info stored in userSubscriptions
          const hasStoredPaymentMethod = !!userData.lastPaymentMethodId;
          setHasDefaultPaymentMethod(hasStoredPaymentMethod);
          
          // If they have a saved payment method, fetch the card details
          if (hasStoredPaymentMethod) {
            try {
              const cardResponse = await fetch('/api/get-payment-method-details', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ uid })
              });

              if (cardResponse.ok) {
                const cardData = await cardResponse.json();
                setSavedCardDetails(cardData);
                console.log('🔧 Saved card details:', cardData);
              }
            } catch (error) {
              console.warn('Could not fetch saved card details:', error);
            }
          }
          
          console.log('🔧 Customer loaded:', {
            stripeId,
            hasStoredPaymentMethod,
            lastPaymentMethodId: userData.lastPaymentMethodId
          });
        }
      } catch (err) {
        console.error('Error loading customer info:', err);
      }
    };

    loadCustomerInfo();
  }, [uid]);

  // Create subscription and handle the response
  const createSubscription = async () => {
    if (!selectedPlan || !customerId) {
      const errorMsg = `Missing required information for upgrade. Plan: ${!!selectedPlan}, Customer: ${!!customerId}`;
      console.error('❌', errorMsg);
      setError(errorMsg);
      return null;
    }

    try {
      console.log('🔧 Creating subscription for customer:', customerId);
      console.log('🔧 Selected plan:', selectedPlan);
      
      setStatusMessage('Creating your subscription...');
      
      const requestBody = {
        uid,
        newPlanBundle: selectedPlan
      };
      
      // Add timeout to prevent endless loading
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 30000); // 30 second timeout
      
      try {
        console.log('🔧 Starting fetch request to /api/create-subscription-checkout...');
        
        const response = await fetch('/api/create-subscription-checkout', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(requestBody),
          signal: controller.signal
        });

        console.log('🔧 Response received!');
        console.log('🔧 Response status:', response.status);

        const result = await response.json();
        console.log('🔧 Response result:', result);

        if (!response.ok) {
          throw new Error(result.error || 'Failed to create subscription');
        }

        console.log('✅ Subscription created successfully:', result);
        
        // Handle the response based on payment requirement
        if (!result.paymentRequired) {
          // Subscription was activated immediately with existing payment method!
          setStatusMessage('Your subscription has been activated successfully!');
          console.log('✅ Subscription activated immediately with existing payment method');
          
          // Call success callback after a brief delay to show the success message
          setTimeout(() => {
            onUpgradeComplete?.(result);
          }, 1500);
          
          return result;
                } else if (result.requires3DS) {
          // 3DS authentication is required - show the Payment Element for proper 3DS UI
          setStatusMessage('Please complete authentication with your bank...');
          console.log('🔧 3DS authentication required - showing payment form for 3DS');
          setClientSecret(result.clientSecret);
          setSubscriptionId(result.subscriptionId);
          setShowPaymentForm(true);
          onPaymentFormToggle?.(true);
          
          return result;
        } else {
          // Payment method failed, show payment form
          setStatusMessage('Please try a different payment method.');
          setClientSecret(result.clientSecret);
          setSubscriptionId(result.subscriptionId);
          setShowPaymentForm(true);
          onPaymentFormToggle?.(true);
          
          return result;
        }
      } finally {
        clearTimeout(timeoutId);
      }
    } catch (err) {
      console.error('❌ Subscription creation error:', err);
      
      if (err.name === 'AbortError') {
        setError('Request timed out. Please check your connection and try again.');
      } else if (err.message.includes('Failed to fetch')) {
        setError('Network error. Please check your connection and try again.');
      } else {
        setError(err.message || 'Failed to create subscription. Please try again.');
      }
      throw err;
    }
  };

  const handleUpgrade = async () => {
    setIsProcessing(true);
    setError(null);
    setStatusMessage('');
    onUpgradeStart?.();

    try {
      await createSubscription();
    } catch (err) {
      onUpgradeError?.(err);
    } finally {
      setIsProcessing(false);
    }
  };

  const isDisabled = !selectedPlan || isUpgrading || isProcessing;

  // Show success message
  if (statusMessage && statusMessage.includes('activated successfully')) {
    return (
      <div className="text-center space-y-4">
        <div className="p-6 rounded-xl bg-green-500/20 border border-green-500/30">
          <div className="flex items-center justify-center mb-3">
            <svg className="w-8 h-8 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <p className="text-green-200 font-medium">{statusMessage}</p>
        </div>
      </div>
    );
  }

  // Show payment form with Elements when we have a client secret
  if (showPaymentForm && clientSecret) {
    return (
      <div className="space-y-2">
        {statusMessage && (
          <div className="text-center mb-4">
            <p className="text-white/80">{statusMessage}</p>
          </div>
        )}
        <Elements 
          stripe={stripePromise}
          options={{
            clientSecret,
            appearance: {
              theme: 'night',
              variables: {
                colorPrimary: '#6366f1',
                colorBackground: 'rgba(255, 255, 255, 0.05)',
                colorText: '#ffffff',
                colorDanger: '#ef4444',
                fontFamily: 'system-ui, sans-serif',
                spacingUnit: '4px',
                borderRadius: '12px',
              },
              rules: {
                '.Input': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  color: '#ffffff',
                  minWidth: '300px',
                  maxWidth: '600px',
                },
                '.Input:focus': {
                  border: '1px solid #6366f1',
                  boxShadow: '0 0 0 2px rgba(99, 102, 241, 0.2)',
                },
                '.Block': {
                  width: '100%',
                  minWidth: '300px',
                },
                '.TabContainer': {
                  width: '100%',
                  minWidth: '300px',
                },
                '.Row': {
                  width: '100%',
                  minWidth: '300px',
                }
              }
            }
          }}
        >
          <PaymentForm
            clientSecret={clientSecret}
            uid={uid}
            selectedPlan={selectedPlan}
            subscriptionId={subscriptionId}
            savedCardDetails={savedCardDetails}
            onUpgradeComplete={onUpgradeComplete}
            onUpgradeError={onUpgradeError}
            onPaymentFormToggle={onPaymentFormToggle}
          />
        </Elements>
      </div>
    );
  }

  // Show loading state if payment form requested but no client secret yet
  if (showPaymentForm && !clientSecret) {
    return (
      <div className="flex justify-center items-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
        <span className="ml-3 text-white">Creating subscription...</span>
      </div>
    );
  }

  // Show processing state
  if (isProcessing && statusMessage) {
    return (
      <div className="text-center space-y-4">
        <div className="flex justify-center items-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
          <span className="ml-3 text-white">{statusMessage}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {error && (
        <div className="p-4 rounded-xl bg-red-500/20 border border-red-500/30">
          <p className="text-red-200 text-sm">{error}</p>
        </div>
      )}

      {/* Saved Card Display */}
      {hasDefaultPaymentMethod && savedCardDetails && (
        <div className="p-4 rounded-xl bg-white/5 border border-white/10 backdrop-blur">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-7 rounded bg-gradient-to-r from-blue-500 to-purple-600 flex items-center justify-center">
                <span className="text-white text-xs font-bold">
                  {savedCardDetails.brand?.toUpperCase() || 'CARD'}
                </span>
              </div>
              <div>
                <p className="text-white font-medium">
                  •••• •••• •••• {savedCardDetails.last4}
                </p>
                <p className="text-white/60 text-sm">
                  Expires {savedCardDetails.expMonth?.toString().padStart(2, '0')}/{savedCardDetails.expYear}
                </p>
              </div>
            </div>
            <div className="text-green-400">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
          </div>
        </div>
      )}

      <button
        onClick={handleUpgrade}
        disabled={isDisabled}
        className={`
          w-full max-w-md py-4 px-6 rounded-xl font-medium transition-all duration-200 flex items-center justify-center gap-2
          ${!isDisabled
            ? 'bg-green-500/40 border border-green-400/50 text-white hover:bg-green-500/50 shadow-lg shadow-green-500/20'
            : 'bg-gray-500/20 border border-gray-500/30 text-gray-400 cursor-not-allowed'
          }
        `}
      >
        {isProcessing ? (
          <>
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
            Processing...
          </>
        ) : (
          <>
            {hasDefaultPaymentMethod ? (
              <>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                </svg>
                {savedCardDetails ? `Pay with •••• ${savedCardDetails.last4}` : 'Upgrade Plan'}
              </>
            ) : (
              "Upgrade Plan"
            )}
          </>
        )}
      </button>

      {hasDefaultPaymentMethod && (
        <button
          onClick={() => {
            setShowPaymentForm(true);
            onPaymentFormToggle?.(true);
          }}
          className="w-full text-center text-indigo-400 text-sm hover:text-indigo-300 transition-colors"
        >
          Use a different payment method →
        </button>
      )}
    </div>
  );
}

StripeUpgradeButton.propTypes = {
  uid: PropTypes.string.isRequired,
  selectedPlan: PropTypes.object,
  isUpgrading: PropTypes.bool,
  onUpgradeStart: PropTypes.func,
  onUpgradeComplete: PropTypes.func,
  onUpgradeError: PropTypes.func,
  onPaymentFormToggle: PropTypes.func,
  stripePromise: PropTypes.object.isRequired
}; 